import { decorateAudioFrequency } from "./helpers/AudioAnalyzerCalculations";
import { AudioAnalyzerOptions, BassSpectrumOptions, WideSpectrumOptions } from "./configuration/AudioAnalyzerOptions";
import { AudioAnalyzerConstants, EmptyAudioFrequency } from "./configuration/AudioAnalyzerConstants";
import { createFFTAnalyzer } from "./helpers/AudioAnalyzerFFT";
import { DEFAULT_AUDIO_TRACK } from "../CreatePageConstants";
import { DoubleFrames } from "../Preview/ProcessSpectrum";

let defaultBassFrequencies = [];
let defaultWideFrequencies = [];

const loadDefaultFrequencies = () => {
    // Read the array with 30fps and double it to 60fps.
    fetch(DEFAULT_AUDIO_TRACK.BASS_FREQUENCIES_FILE_PATH)
        .then(response => response.json())
        .then(data => { defaultBassFrequencies = DoubleFrames(data); });
    
    // Read the array with 30fps and double it to 60fps.
    fetch(DEFAULT_AUDIO_TRACK.WIDE_FREQUENCIES_FILE_PATH)
        .then(response => response.json())
        .then(data => { defaultWideFrequencies = DoubleFrames(data); });
}

// Load default frequencies as soon as the module is loaded.
loadDefaultFrequencies();


// The real time audio analyser is connecting to existing audio element on the page.
// Call this method to connect to the necessary audio element.
const attachElement = (audioElement) => {
    if (window.realtimeAnalyzerContext && window.realtimeAnalyzerContext.audioElement) {
        throw `Can't attach the element because the another one is already attached!`;
    }

    window.realtimeAnalyzerContext = {
        audioElement: audioElement,
        analyzerNode: undefined,
        audioContext: undefined,
        gainNode: undefined,
        delayNode: undefined,
        
        bassMaxMagnitude: undefined,
        wideMaxMagnitude: undefined,

        historicalBassFrequencies: [],
        historicalWideFrequencies: [],

        defaultAudioVolume: 0,

        bassExponent: BassSpectrumOptions.exponent,
        bassRoot: BassSpectrumOptions.root,
        bassTargetMax: BassSpectrumOptions.magnitudeTargetMax,
        bassBaseBarCount: BassSpectrumOptions.baseBarCount,

        wideExponent: WideSpectrumOptions.exponent,
        wideRoot: WideSpectrumOptions.root,
        wideTargetMax: WideSpectrumOptions.magnitudeTargetMax,
        wideBaseBarCount: WideSpectrumOptions.baseBarCount,
    };
};


// The real time audio analyser is connecting to existing audio element on the page.
// Call this method to disconnect from the audio element.
// All the resources of audio analyzer will be removed. 
const detachElement = () => {
    if (!window.realtimeAnalyzerContext && !window.realtimeAnalyzerContext.audioElement)
        return;

    if (!window.realtimeAnalyzerContext.audioElement.paused)
        window.realtimeAnalyzerContext.audioElement.pause();

    removeAudioNodes();
    window.realtimeAnalyzerContext = undefined;
};


const createAudioNodes = () => {
    if (!window.realtimeAnalyzerContext && !window.realtimeAnalyzerContext.audioElement) {
        throw `The audio element is missing. Need to attach it first!`;
    }

    // Create the custom fft processor for audio analysis.
    const getByteFrequencyData = createFFTAnalyzer(
        AudioAnalyzerOptions.fftSize, 
        AudioAnalyzerOptions.minDecibels,
        AudioAnalyzerOptions.maxDecibels, 
        AudioAnalyzerOptions.smoothingTime
    );

    // Create the audio context.
    const audioContext = new AudioContext();

    // Create the analyzer node.
    const analyzerNode = audioContext.createAnalyser();
    analyzerNode.fftSize = AudioAnalyzerOptions.fftSize;

    // No need the smoothing here because we use the custom fft processor.
    analyzerNode.smoothingTimeConstant = 0;

    // Connect the media to analyzer node.
    const mediaSource = audioContext.createMediaElementSource(window.realtimeAnalyzerContext.audioElement);
    mediaSource.connect(analyzerNode);

    // Connect the analyzer node to gain node (the proxy node to control the volume)
    const gainNode = audioContext.createGain();
    gainNode.gain.value = window.realtimeAnalyzerContext.defaultAudioVolume;
    analyzerNode.connect(gainNode);

    // Connect the delay node.
    const delayNode = new DelayNode(audioContext, { delayTime: 0.15 });
    gainNode.connect(delayNode);

    // Connect the delay node to speakers.
    delayNode.connect(audioContext.destination);

    window.realtimeAnalyzerContext.analyzerNode = analyzerNode;
    window.realtimeAnalyzerContext.audioContext = audioContext;
    window.realtimeAnalyzerContext.mediaSource = mediaSource;
    window.realtimeAnalyzerContext.gainNode = gainNode;
    window.realtimeAnalyzerContext.delayNode = delayNode;
    window.realtimeAnalyzerContext.getByteFrequencyData = getByteFrequencyData;
};


const removeAudioNodes = () => {
    const nodesCreated = window.realtimeAnalyzerContext.delayNode 
        && window.realtimeAnalyzerContext.gainNode 
        && window.realtimeAnalyzerContext.analyzerNode 
        && window.realtimeAnalyzerContext.mediaSource;

    if (!nodesCreated)
        return;

    // The order of nodes to disconnect is important here.
    window.realtimeAnalyzerContext.delayNode.disconnect(window.realtimeAnalyzerContext.audioContext.destination);
    window.realtimeAnalyzerContext.gainNode.disconnect(window.realtimeAnalyzerContext.delayNode);
    window.realtimeAnalyzerContext.analyzerNode.disconnect(window.realtimeAnalyzerContext.gainNode);
    window.realtimeAnalyzerContext.mediaSource.disconnect(window.realtimeAnalyzerContext.analyzerNode);

    // Note:
    // The mediaSource should be removed by browser when we change the src of the audio element.
    // But there is no 100% information.

    window.realtimeAnalyzerContext.audioContext.close();
    console.log('The audio nodes have been removed!');
}


const playAudio = () => {
    if (!window.realtimeAnalyzerContext || !window.realtimeAnalyzerContext.audioElement)
        throw `Can't play the audio. Attach the audio element first!`;

    if (!window.realtimeAnalyzerContext.analyzerNode) {
        // We create the the audio nodes on play to fix an issue with first user click after page load.
        createAudioNodes();
    }

    window.realtimeAnalyzerContext.audioElement.play();
};


const pauseAudio = () => {
    if (!window.realtimeAnalyzerContext || !window.realtimeAnalyzerContext.audioElement)
        throw `Can't pause the audio. Attach the audio element first!`;

    window.realtimeAnalyzerContext.audioElement.pause();
};


// Returns the current audio time in seconds.
const getCurrentAudioTime = () => {
    if (!window.realtimeAnalyzerContext.audioElement)
        return 0;

    return window.realtimeAnalyzerContext.audioElement.currentTime;
};


// Sets the current audio time in seconds.
const setCurrentAudioTime = (value) => {
    if (!window.realtimeAnalyzerContext.audioElement)
        return;

    window.realtimeAnalyzerContext.audioElement.currentTime = value;
};


const setAudioVolume = (value) => {
    if (value < 0 || value > 1)
        return;

    window.realtimeAnalyzerContext.defaultAudioVolume = value;

    if (!window.realtimeAnalyzerContext.gainNode)
        return;
    
    window.realtimeAnalyzerContext.gainNode.gain.value = value;
}


const getAudioVolume = () => {
    if (window.realtimeAnalyzerContext.gainNode)
        return window.realtimeAnalyzerContext.gainNode.gain.value;
}


const isAudioPaused = () => {
    if (!window.realtimeAnalyzerContext)
        throw `The audio context is missing. Attach the audio element first!`;

    return window.realtimeAnalyzerContext.audioElement.paused;
}


const setMaxMagnitudes = (bassMaxMagnitude, wideMaxMagnitude) => {
    if (!window.realtimeAnalyzerContext)
        throw `The audio context is missing. Attach the audio element first!`;

    window.realtimeAnalyzerContext.bassMaxMagnitude = bassMaxMagnitude;
    window.realtimeAnalyzerContext.wideMaxMagnitude = wideMaxMagnitude;

    console.log('Max magnitudes (Realtime audio analyzer):', bassMaxMagnitude, wideMaxMagnitude);
};


const getCurrentBassFrequency = () => {
    if (!window.realtimeAnalyzerContext || !window.realtimeAnalyzerContext.analyzerNode) {
        return EmptyAudioFrequency;
    }

    let bassFrequency = undefined;
    let cumulativeMaxMagnitude = undefined;

    if (window.realtimeAnalyzerContext.bassMaxMagnitude) {
        // If we have the predefined max magnitude,
        // Get the frequency from the audio analyzer in real-time.
        let timeDomainData = new Float32Array(window.realtimeAnalyzerContext.analyzerNode.fftSize);
        window.realtimeAnalyzerContext.analyzerNode.getFloatTimeDomainData(timeDomainData);
        let clearFrequency = window.realtimeAnalyzerContext.getByteFrequencyData(timeDomainData, AudioAnalyzerOptions.clearFrequencyMaxLength);
        bassFrequency = clearFrequency.slice(BassSpectrumOptions.spectrumStart, BassSpectrumOptions.spectrumEnd);
        cumulativeMaxMagnitude = window.realtimeAnalyzerContext.bassMaxMagnitude;
    } else {
        // Otherwise (fallback),
        // Get the frequency from the predefined object for default audio track.
        // In this case user will see the default visualization but with his own audio.
        const currentTime = getCurrentAudioTime() % DEFAULT_AUDIO_TRACK.DURATION;
        const frequenciesIndex = Math.floor(currentTime * DEFAULT_AUDIO_TRACK.PREDEFINED_BASS_FREQUENCIES_FPS);
        bassFrequency = [...defaultBassFrequencies[frequenciesIndex]];
        cumulativeMaxMagnitude = DEFAULT_AUDIO_TRACK.BASS_MAX_MAGNITUDE;
    }

    bassFrequency = decorateAudioFrequency(
        bassFrequency,
        window.realtimeAnalyzerContext.bassTargetMax,
        window.realtimeAnalyzerContext.bassExponent,
        window.realtimeAnalyzerContext.bassRoot,
        window.realtimeAnalyzerContext.bassBaseBarCount,
        cumulativeMaxMagnitude
    );
    
    return bassFrequency;
};


const getCurrentWideFrequency = () => {
    if (!window.realtimeAnalyzerContext || !window.realtimeAnalyzerContext.analyzerNode)
        return EmptyAudioFrequency;

    let wideFrequency = undefined;
    let cumulativeMaxMagnitude = undefined;

    if (window.realtimeAnalyzerContext.wideMaxMagnitude) {
        // If we have the predefined max magnitude,
        // Get the frequency from the audio analyzer in real-time.
        let timeDomainData = new Float32Array(window.realtimeAnalyzerContext.analyzerNode.fftSize);
        window.realtimeAnalyzerContext.analyzerNode.getFloatTimeDomainData(timeDomainData);
        let clearFrequency = window.realtimeAnalyzerContext.getByteFrequencyData(timeDomainData, AudioAnalyzerOptions.clearFrequencyMaxLength);
        wideFrequency = clearFrequency.slice(WideSpectrumOptions.spectrumStart, WideSpectrumOptions.spectrumEnd);
        cumulativeMaxMagnitude = window.realtimeAnalyzerContext.wideMaxMagnitude;
    } else {
        // Otherwise (fallback),
        // Get the frequency from the predefined object for default audio track.
        // In this case user will see the default visualization but with his own audio.
        const currentTime = getCurrentAudioTime() % DEFAULT_AUDIO_TRACK.DURATION;
        const frequenciesIndex = Math.floor(currentTime * DEFAULT_AUDIO_TRACK.PREDEFINED_WIDE_FREQUENCIES_FPS);
        wideFrequency = [...defaultWideFrequencies[frequenciesIndex]];
        cumulativeMaxMagnitude = DEFAULT_AUDIO_TRACK.WIDE_MAX_MAGNITUDE;
    }
    
    wideFrequency = decorateAudioFrequency(
        wideFrequency,
        window.realtimeAnalyzerContext.wideTargetMax,
        window.realtimeAnalyzerContext.wideExponent,
        window.realtimeAnalyzerContext.wideRoot,
        window.realtimeAnalyzerContext.wideBaseBarCount,
        cumulativeMaxMagnitude
    );
    
    return wideFrequency;
};


// This function builds the chain of frequencies.
// The current one and few past frequencies (for layers delay)
const concatHistoricalFrequencies = (currentFrequency, historicalFrequencies, limit) => {
    // Insert the new item at the beginning.
    historicalFrequencies.unshift(currentFrequency);

    // If the array is not full, insert the same item to the end.
    if (historicalFrequencies.length < limit) {
        for (let i = historicalFrequencies.length; i < limit; i++) {
            historicalFrequencies.push(currentFrequency);
        }
        return historicalFrequencies;
    }
    
    // If the array is over the limit, remove old items to fit the limit.
    if (historicalFrequencies.length > limit) {
        historicalFrequencies.splice(limit, (historicalFrequencies.length - limit));
        return historicalFrequencies;
    }
    
    return historicalFrequencies;
}


// Returns the array with current and past frequencies for bass audio
const getHistoricalBassFrequencies = (includeNextFrame = true) => {
    if (!includeNextFrame && Boolean(window.realtimeAnalyzerContext.historicalBassFrequencies.length))
        return window.realtimeAnalyzerContext.historicalBassFrequencies;

    let currentBassFrequency = getCurrentBassFrequency();

    concatHistoricalFrequencies(
        currentBassFrequency,
        window.realtimeAnalyzerContext.historicalBassFrequencies,
        AudioAnalyzerConstants.HistoricalFrequenciesLimit
    );
    
    return window.realtimeAnalyzerContext.historicalBassFrequencies;
}
        

// Returns the array with current and past frequencies for wide audio
const getHistoricalWideFrequencies = (includeNextFrame = true) => {
    if (!includeNextFrame && Boolean(window.realtimeAnalyzerContext.historicalWideFrequencies.length))
        return window.realtimeAnalyzerContext.historicalWideFrequencies;

    let currentWideFrequency = getCurrentWideFrequency();

    concatHistoricalFrequencies(
        currentWideFrequency,
        window.realtimeAnalyzerContext.historicalWideFrequencies,
        AudioAnalyzerConstants.HistoricalFrequenciesLimit
    );

    return window.realtimeAnalyzerContext.historicalWideFrequencies;
}


// RealtimeAudioAnalyzer - calculates the audio frequency in the real-time (audio element is playing - frequency is returning).
// The audio element should be provided.
// Need to use this object to change audio position or volume.

export const RealtimeAudioAnalyzer = {
    attachElement: (audioElement) => attachElement(audioElement),
    detachElement: () => detachElement(),

    getCurrentBassFrequency: () => getCurrentBassFrequency(),
    getCurrentWideFrequency: () => getCurrentWideFrequency(),

    getHistoricalBassFrequencies: (includeNextFrame = true) => getHistoricalBassFrequencies(includeNextFrame),
    getHistoricalWideFrequencies: (includeNextFrame = true) => getHistoricalWideFrequencies(includeNextFrame),
    
    getHistoricalFrequencies: (includeNextFrame = true) => {
        return {
            bass: getHistoricalBassFrequencies(includeNextFrame),
            wide: getHistoricalWideFrequencies(includeNextFrame)
        }
    },

    clearHistoricalBassFrequencies: () => { window.realtimeAnalyzerContext.historicalBassFrequencies = []; },
    clearHistoricalWideFrequencies: () => { window.realtimeAnalyzerContext.historicalWideFrequencies = []; },

    setMaxMagnitudes: (bassMaxMagnitude, wideMaxMagnitude) => setMaxMagnitudes(bassMaxMagnitude, wideMaxMagnitude),

    setBassExponent: (exponent) => window.realtimeAnalyzerContext.bassExponent = exponent,
    setBassRoot: (root) => window.realtimeAnalyzerContext.bassRoot = root,
    setBassTargetMax: (targetMax) => window.realtimeAnalyzerContext.bassTargetMax = targetMax,

    setWideExponent: (exponent) => window.realtimeAnalyzerContext.wideExponent = exponent,
    setWideRoot: (root) => window.realtimeAnalyzerContext.wideRoot = root,
    setWideTargetMax: (targetMax) => window.realtimeAnalyzerContext.wideTargetMax = targetMax,

    play: () => playAudio(),
    pause: () => pauseAudio(),
    setVolume: (value) => setAudioVolume(value),
    getVolume: () => getAudioVolume(),

    getCurrentTime: () => getCurrentAudioTime(),
    setCurrentTime: (value) => setCurrentAudioTime(value),

    isPaused: () => isAudioPaused(),
};